"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useInternalNotification } from "../contexts/NotificationContext";
import { useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs } from "../components/ui/tabs";
import { SearchBar } from "../components/ui/search-bar";
import {
  Bell,
  Paperclip,
  MessageSquare,
  User,
  Download,
  X,
} from "lucide-react";
import {
  fetchConversations,
  fetchMessages,
  markMessagesAsRead,
  sendMessage,
  formatTime,
  formatMessageTime,
  getDateDivider,
  groupMessagesByDate,
  getSelectedUserProfile,
  filterConversations,
  showToast,
  useConversationWebSocket,
  useNotificationWebSocket,
} from "../utils/conversationsUtils";

const ConversationScreen = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const {
    internalNotifications,
    clearConversation,
    setActiveConversation,
    initializeConversationCounts,
  } = useInternalNotification();

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");

  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [toasts, setToasts] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const processedToastIds = useRef(new Set());

  const handleShowToast = useCallback(
    (message, conversationId, toastId = null) => {
      if (message === "New message" || message === "New message from Someone") {
        return;
      }

      const uniqueId = toastId || `${conversationId}-${message}-${Date.now()}`;

      if (processedToastIds.current.has(uniqueId)) return;
      processedToastIds.current.add(uniqueId);

      setTimeout(() => {
        processedToastIds.current.delete(uniqueId);
      }, 5000);

      showToast(setToasts, message, conversationId);
    },
    [],
  );
  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    const data = await fetchConversations(token);
    const sortedData = data.sort((a, b) => {
      const dateA = a.last_message?.created_at
        ? new Date(a.last_message.created_at)
        : new Date(0);
      const dateB = b.last_message?.created_at
        ? new Date(b.last_message.created_at)
        : new Date(0);
      return dateB - dateA;
    });
    setConversations(sortedData);
    initializeConversationCounts(sortedData);
    setLoadingConversations(false);
    return sortedData;
  }, [token, initializeConversationCounts]);

  const updateConversationPreview = useCallback(
    (conversationId, lastMessage) => {
      setConversations((prevConversations) => {
        const updated = prevConversations.map((conv) => {
          if (conv.id === conversationId) {
            return {
              ...conv,
              last_message: {
                id: lastMessage.id,
                body:
                  lastMessage.body ||
                  (lastMessage.attachment ? "📎 Attachment" : ""),
                created_at: lastMessage.created_at,
                attachment: lastMessage.attachment,
              },
            };
          }
          return conv;
        });

        return updated.sort((a, b) => {
          const dateA = a.last_message?.created_at
            ? new Date(a.last_message.created_at)
            : new Date(0);
          const dateB = b.last_message?.created_at
            ? new Date(b.last_message.created_at)
            : new Date(0);
          return dateB - dateA;
        });
      });
    },
    [],
  );

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const conversationId = searchParams.get("conversation_id");

    if (conversationId && conversations.length > 0) {
      if (selectedConversation?.id === parseInt(conversationId)) return;

      const conversation = conversations.find(
        (c) => c.id === parseInt(conversationId),
      );
      if (conversation) {
        setSelectedConversation(conversation);
      }
    }
  }, [location.search, conversations, selectedConversation?.id]);

  useEffect(() => {
    if (selectedConversation) {
      setActiveConversation(selectedConversation.id);

      const loadMessages = async () => {
        setLoadingMessages(true);
        const data = await fetchMessages(selectedConversation.id, token);
        const sortedMessages = data.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        );
        setMessages(sortedMessages);
        setLoadingMessages(false);

        try {
          await markMessagesAsRead(selectedConversation.id, token);
          clearConversation(selectedConversation.id);
        } catch (error) {}
      };

      loadMessages();

      return () => {
        setActiveConversation(null);
      };
    }
  }, [selectedConversation, token, clearConversation, setActiveConversation]);

  const handleNewMessage = useCallback(
    (message) => {
      setMessages((prev) => {
        if (prev.some((msg) => msg.id === message.id)) return prev;
        return [...prev, message].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        );
      });

      if (message.conversation_id) {
        updateConversationPreview(message.conversation_id, message);
      }
    },
    [updateConversationPreview],
  );

  useConversationWebSocket(
    selectedConversation,
    user,
    handleNewMessage,
    setIsTyping,
  );

  useNotificationWebSocket(
    token,
    selectedConversation,
    conversations,
    loadConversations,
    handleShowToast,
    user,
    updateConversationPreview,
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handleNotificationEvent = (event) => {
      const { message, conversationId, toastId } = event.detail;
      if (message) {
        const uniqueId = toastId || `${conversationId}-${Date.now()}`;
        handleShowToast(message, conversationId, uniqueId);
      }
    };
    window.addEventListener("notification:show-toast", handleNotificationEvent);
    return () => {
      window.removeEventListener(
        "notification:show-toast",
        handleNotificationEvent,
      );
    };
  }, [handleShowToast]);

  const handleConversationClick = (conv) => {
    setSelectedConversation(conv);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => setFilePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
    event.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    const messageText = newMessage;
    const fileToSend = selectedFile;
    const tempId = `optimistic-${Date.now()}`;

    const optimisticMessage = {
      id: tempId,
      body: messageText,
      sender_id: user.id,
      created_at: new Date().toISOString(),
      attachment: fileToSend ? { filename: fileToSend.name, url: null } : null,
      conversation_id: selectedConversation.id,
    };

    setMessages((prev) =>
      [...prev, optimisticMessage].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at),
      ),
    );

    updateConversationPreview(selectedConversation.id, optimisticMessage);

    setNewMessage("");
    setSelectedFile(null);
    setFilePreview(null);

    setIsSending(true);
    try {
      await sendMessage(
        selectedConversation.id,
        messageText,
        token,
        fileToSend,
      );
    } catch (error) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setNewMessage(messageText);
      loadConversations();
    } finally {
      setIsSending(false);
    }
  };

  const getConversationName = (conversation) => {
    if (conversation.sender?.id === user?.id) {
      return conversation.recipient
        ? `${conversation.recipient.first_name} ${conversation.recipient.last_name}`
        : `Client #${conversation.recipient_id}`;
    } else {
      return conversation.sender
        ? `${conversation.sender.first_name} ${conversation.sender.last_name}`
        : `Professional #${conversation.sender_id}`;
    }
  };

  const filteredConversations = filterConversations(
    conversations,
    searchTerm,
    activeFilter,
    internalNotifications,
    user,
  );

  const selectedUser = getSelectedUserProfile(selectedConversation, user);
  const groupedMessages = groupMessagesByDate(messages, getDateDivider);

  const renderMessageContent = (msg) => {
    const hasAttachment = msg.attachment && msg.attachment.url;
    const hasBody = msg.body && msg.body.trim().length > 0;

    return (
      <>
        {hasBody && <div style={styles.messageText}>{msg.body}</div>}
        {hasAttachment && (
          <div style={styles.attachmentContainer}>
            {msg.attachment.content_type?.startsWith("image/") ? (
              <div style={styles.imageAttachment}>
                <img
                  src={msg.attachment.url}
                  alt={msg.attachment.filename}
                  style={styles.attachmentImage}
                  onClick={() => window.open(msg.attachment.url, "_blank")}
                />
              </div>
            ) : (
              <div style={styles.fileAttachment}>
                <Paperclip size={16} />
                <a
                  href={msg.attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.attachmentLink}
                >
                  {msg.attachment.filename}
                </a>
                <Download size={16} style={styles.downloadIcon} />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.toastContainer}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={styles.toast}
            onClick={() => {
              if (toast.conversationId) {
                const conversation = conversations.find(
                  (c) => c.id === parseInt(toast.conversationId),
                );
                if (conversation) {
                  setSelectedConversation(conversation);
                  setToasts((prev) => prev.filter((t) => t.id !== toast.id));
                }
              }
            }}
            role="button"
            tabIndex={0}
          >
            <MessageSquare size={16} color="#F97316" />
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              style={styles.toastCloseButton}
              onClick={(e) => {
                e.stopPropagation();
                setToasts((prev) => prev.filter((t) => t.id !== toast.id));
              }}
            >
              <X size={14} color="#6b7280" />
            </button>
          </div>
        ))}
      </div>

      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Messages</h1>
        <Button variant="ghost" icon={Bell} />
      </div>

      <div style={styles.mainContent}>
        <div style={styles.sidebar}>
          <div style={styles.searchContainer}>
            <SearchBar
              placeholder="Search conversations"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={styles.filterTabsContainer}>
            <Tabs
              filters={["All", "Read", "Unread"]}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />
          </div>

          <div style={styles.conversationsList}>
            {loadingConversations && conversations.length === 0 && (
              <p style={styles.emptyText}>Loading…</p>
            )}
            {!loadingConversations && filteredConversations.length === 0 && (
              <p style={styles.emptyText}>No conversations found.</p>
            )}

            {filteredConversations.map((conv) => {
              const unreadCount =
                internalNotifications.byConversation[String(conv.id)] || 0;
              const isSelected = selectedConversation?.id === conv.id;
              const conversationName = getConversationName(conv);
              const displayUser =
                conv.sender?.id === user?.id ? conv.recipient : conv.sender;

              return (
                <div
                  key={conv.id}
                  style={{
                    ...styles.conversationItem,
                    ...(isSelected ? styles.conversationItemActive : {}),
                  }}
                  onClick={() => handleConversationClick(conv)}
                >
                  {unreadCount > 0 && <div style={styles.unreadDot} />}

                  <div style={styles.avatarContainer}>
                    {displayUser?.avatar_url ? (
                      <img
                        src={displayUser.avatar_url || "/placeholder.svg"}
                        alt={conversationName}
                        style={styles.avatar}
                      />
                    ) : (
                      <div style={styles.avatarPlaceholder}>
                        {displayUser
                          ? `${displayUser.first_name?.charAt(0)}${displayUser.last_name?.charAt(0)}`
                          : "?"}
                      </div>
                    )}
                  </div>

                  <div style={styles.conversationDetails}>
                    <div style={styles.conversationHeader}>
                      <span
                        style={{
                          ...styles.conversationName,
                          fontWeight: unreadCount > 0 ? "600" : "500",
                        }}
                      >
                        {conversationName}
                      </span>
                      <span style={styles.conversationTime}>
                        {conv.last_message?.created_at
                          ? formatTime(conv.last_message.created_at)
                          : ""}
                      </span>
                    </div>
                    <p style={styles.conversationPreview}>
                      {conv.last_message?.body ||
                        (conv.last_message?.attachment
                          ? "📎 Attachment"
                          : "No messages yet")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={styles.chatArea}>
          {selectedConversation ? (
            <>
              <div style={styles.messagesContainer}>
                {loadingMessages ? (
                  <div style={styles.loadingContainer}>
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div style={styles.emptyMessages}>
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date}>
                      <div style={styles.dateDivider}>
                        <span style={styles.dateDividerText}>{date}</span>
                      </div>
                      {msgs.map((msg) => {
                        const isCurrentUser = msg.sender_id === user.id;
                        let senderName = "";
                        let otherUser = null;

                        if (isCurrentUser) {
                          senderName = "You";
                        } else {
                          otherUser =
                            selectedConversation.sender?.id === user.id
                              ? selectedConversation.recipient
                              : selectedConversation.sender;
                          senderName = otherUser
                            ? `${otherUser.first_name} ${otherUser.last_name}`
                            : "Client";
                        }

                        return (
                          <div
                            key={msg.id}
                            style={{
                              ...styles.messageWrapper,
                              justifyContent: isCurrentUser
                                ? "flex-end"
                                : "flex-start",
                            }}
                          >
                            {!isCurrentUser && (
                              <div style={styles.messageAvatar}>
                                {otherUser?.avatar_url ? (
                                  <img
                                    src={
                                      otherUser.avatar_url || "/placeholder.svg"
                                    }
                                    alt={senderName}
                                    style={styles.messageAvatarImg}
                                  />
                                ) : (
                                  <div style={styles.messageAvatarPlaceholder}>
                                    {otherUser?.first_name?.charAt(0) || "?"}
                                  </div>
                                )}
                              </div>
                            )}

                            <div style={styles.messageContent}>
                              <div style={styles.messageHeader}>
                                <span style={styles.messageSender}>
                                  {senderName}
                                </span>
                                <span style={styles.messageTime}>
                                  {formatMessageTime(msg.created_at)}
                                </span>
                              </div>
                              <div
                                style={{
                                  ...styles.messageBubble,
                                  ...(isCurrentUser
                                    ? styles.sentBubble
                                    : styles.receivedBubble),
                                }}
                              >
                                {renderMessageContent(msg)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}

                {isTyping && (
                  <div style={styles.messageWrapper}>
                    <div style={styles.messageAvatar}>
                      {selectedUser?.avatar_url ? (
                        <img
                          src={selectedUser.avatar_url || "/placeholder.svg"}
                          alt="Typing"
                          style={styles.messageAvatarImg}
                        />
                      ) : (
                        <div style={styles.messageAvatarPlaceholder}>
                          {selectedUser?.first_name?.charAt(0) || "?"}
                        </div>
                      )}
                    </div>
                    <div style={styles.typingIndicator}>
                      <span style={styles.typingDot} />
                      <span
                        style={{ ...styles.typingDot, animationDelay: "0.2s" }}
                      />
                      <span
                        style={{ ...styles.typingDot, animationDelay: "0.4s" }}
                      />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div style={styles.inputContainer}>
                {selectedFile && (
                  <div style={styles.filePreviewContainer}>
                    {filePreview ? (
                      <div style={styles.imagePreviewWrapper}>
                        <img
                          src={filePreview}
                          alt="Preview"
                          style={styles.imagePreview}
                        />
                        <button
                          style={styles.removeFileButton}
                          onClick={handleRemoveFile}
                          type="button"
                        >
                          <X size={14} color="#fff" />
                        </button>
                      </div>
                    ) : (
                      <div style={styles.filePreviewItem}>
                        <Paperclip size={16} color="#6b7280" />
                        <span style={styles.filePreviewName}>
                          {selectedFile.name}
                        </span>
                        <button
                          style={styles.removeFileButtonSmall}
                          onClick={handleRemoveFile}
                          type="button"
                        >
                          <X size={14} color="#6b7280" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div style={styles.inputWrapper}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                  />
                  <input
                    type="text"
                    placeholder="Send a message"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSending) handleSendMessage();
                    }}
                    style={styles.messageInput}
                    disabled={isSending}
                  />
                  <div style={styles.inputActions}>
                    <button
                      style={styles.attachButton}
                      onClick={handleAttachClick}
                      type="button"
                      disabled={isSending}
                    >
                      <Paperclip size={20} color="#6b7280" />
                    </button>
                    <Button
                      variant="primary"
                      onClick={handleSendMessage}
                      disabled={
                        (!newMessage.trim() && !selectedFile) || isSending
                      }
                    >
                      {isSending ? "Sending..." : "Send"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={styles.emptyConversation}>
              <MessageSquare size={48} color="#d1d5db" />
              <h3 style={styles.emptyTitle}>Select a conversation</h3>
              <p style={styles.emptySubtitle}>
                Choose a conversation from the sidebar to start chatting
              </p>
            </div>
          )}
        </div>

        {selectedUser && (
          <div style={styles.profileSidebar}>
            <div style={styles.profileBanner} />
            <div style={styles.profileInfo}>
              <div style={styles.profileAvatarContainer}>
                {selectedUser.avatar_url ? (
                  <img
                    src={selectedUser.avatar_url || "/placeholder.svg"}
                    alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                    style={styles.profileAvatar}
                  />
                ) : (
                  <div style={styles.profileAvatarPlaceholder}>
                    <User size={40} color="#9ca3af" />
                  </div>
                )}
              </div>

              <div style={styles.profileName}>
                <span>
                  {selectedUser.first_name} {selectedUser.last_name}
                </span>
              </div>

              {selectedUser.bio && (
                <div style={styles.profileSection}>
                  <h4 style={styles.profileSectionTitle}>About</h4>
                  <p style={styles.profileSectionText}>{selectedUser.bio}</p>
                </div>
              )}

              <div style={styles.profileStats}>
                <div style={styles.profileStat}>
                  <span style={styles.profileStatLabel}>
                    Total Orders Placed
                  </span>
                  <span style={styles.profileStatValue}>
                    {selectedUser.total_orders || 0} Orders
                  </span>
                </div>
                <div style={styles.profileStat}>
                  <span style={styles.profileStatLabel}>Orders Completed</span>
                  <span style={styles.profileStatValue}>
                    {selectedUser.completed_orders || 0} Completed
                  </span>
                </div>
                <div style={styles.profileStat}>
                  <span style={styles.profileStatLabel}>
                    Pending/In Progress
                  </span>
                  <span style={styles.profileStatValue}>
                    {selectedUser.pending_orders || 0} Pending
                  </span>
                </div>
              </div>

              {selectedUser.current_order && (
                <div style={styles.currentOrderCard}>
                  <span style={styles.currentOrderLabel}>Current order</span>
                  <div style={styles.currentOrderDetails}>
                    <span style={styles.currentOrderService}>
                      {selectedUser.current_order.service_name}
                      {selectedUser.current_order.additional_count > 0 && (
                        <span style={styles.additionalBadge}>
                          +{selectedUser.current_order.additional_count}
                        </span>
                      )}
                    </span>
                    <span style={styles.currentOrderNumber}>
                      #{selectedUser.current_order.order_number}
                    </span>
                  </div>
                  <Button
                    variant="primary"
                    fullWidth
                    style={{ marginTop: "12px" }}
                  >
                    Mark as completed
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: "#ffffff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  toastContainer: {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  toast: {
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    borderLeft: "4px solid #F97316",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  toastCloseButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: "1px solid #e5e7eb",
  },
  headerTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#111827",
    margin: 0,
  },
  mainContent: { display: "flex", flex: 1, overflow: "hidden" },
  sidebar: {
    width: "300px",
    borderRight: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
  },
  searchContainer: { padding: "16px" },
  filterTabsContainer: { padding: "0 16px 16px 16px" },
  conversationsList: { flex: 1, overflowY: "auto" },
  emptyText: { padding: "24px", textAlign: "center", color: "#6b7280" },
  conversationItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #f3f4f6",
    transition: "background-color 0.2s",
    position: "relative",
  },
  conversationItemActive: { backgroundColor: "#f9fafb" },
  unreadDot: {
    position: "absolute",
    left: "18px",
    top: "18px",
    transform: "translateY(-50%)",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: "#F97316",
  },
  avatarContainer: { marginRight: "12px", flexShrink: 0 },
  avatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  avatarPlaceholder: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "600",
    color: "#6b7280",
  },
  conversationDetails: { flex: 1, minWidth: 0 },
  conversationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2px",
  },
  conversationName: { fontSize: "14px", color: "#111827" },
  conversationTime: { fontSize: "12px", color: "#9ca3af" },
  conversationPreview: {
    fontSize: "13px",
    color: "#6b7280",
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
  },
  messagesContainer: { flex: 1, padding: "24px", overflowY: "auto" },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    color: "#6b7280",
  },
  emptyMessages: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    color: "#6b7280",
  },
  dateDivider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "24px 0",
  },
  dateDividerText: {
    fontSize: "13px",
    color: "#6b7280",
    backgroundColor: "#ffffff",
    padding: "0 12px",
  },
  messageWrapper: {
    display: "flex",
    marginBottom: "16px",
    alignItems: "flex-start",
  },
  messageAvatar: { position: "relative", marginRight: "12px", flexShrink: 0 },
  messageAvatarImg: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  messageAvatarPlaceholder: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "600",
    color: "#6b7280",
  },
  messageContent: { maxWidth: "60%" },
  messageHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "4px",
  },
  messageSender: { fontSize: "14px", fontWeight: "500", color: "#111827" },
  messageTime: { fontSize: "12px", color: "#9ca3af" },
  messageBubble: {
    padding: "12px 16px",
    borderRadius: "12px",
    fontSize: "14px",
    lineHeight: "1.5",
  },
  sentBubble: {
    backgroundColor: "#1f2937",
    color: "#ffffff",
    borderTopRightRadius: "4px",
  },
  receivedBubble: {
    backgroundColor: "#f3f4f6",
    color: "#111827",
    borderTopLeftRadius: "4px",
  },
  messageText: { marginBottom: "0" },
  attachmentContainer: { marginTop: "8px" },
  imageAttachment: { maxWidth: "100%" },
  attachmentImage: {
    maxWidth: "300px",
    maxHeight: "300px",
    borderRadius: "8px",
    cursor: "pointer",
    objectFit: "cover",
  },
  fileAttachment: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "8px",
    fontSize: "13px",
  },
  attachmentLink: {
    color: "inherit",
    textDecoration: "none",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  downloadIcon: { flexShrink: 0, opacity: 0.6 },
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "12px 16px",
    backgroundColor: "#f3f4f6",
    borderRadius: "12px",
    borderTopLeftRadius: "4px",
  },
  typingDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#9ca3af",
    animation: "bounce 1s infinite",
  },
  inputContainer: {
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
  },
  messageInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "14px",
    padding: "8px 0",
  },
  inputActions: { display: "flex", alignItems: "center", gap: "8px" },
  attachButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  filePreviewContainer: {
    marginBottom: "12px",
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  imagePreviewWrapper: { position: "relative", display: "inline-block" },
  imagePreview: {
    maxWidth: "120px",
    maxHeight: "120px",
    borderRadius: "8px",
    objectFit: "cover",
    border: "1px solid #e5e7eb",
  },
  removeFileButton: {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#ef4444",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  filePreviewItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "#f3f4f6",
    borderRadius: "8px",
    maxWidth: "300px",
  },
  filePreviewName: {
    fontSize: "13px",
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  removeFileButtonSmall: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  emptyConversation: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#6b7280",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    marginTop: "16px",
    marginBottom: "8px",
  },
  emptySubtitle: { fontSize: "14px", color: "#6b7280" },
  profileSidebar: {
    width: "280px",
    borderLeft: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
    overflow: "auto",
  },
  profileBanner: {
    height: "100px",
    background:
      "linear-gradient(135deg, #c4b5fd 0%, #fbcfe8 50%, #93c5fd 100%)",
  },
  profileInfo: { padding: "0 20px 20px" },
  profileAvatarContainer: { marginTop: "-40px", marginBottom: "12px" },
  profileAvatar: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    border: "4px solid #ffffff",
    objectFit: "cover",
  },
  profileAvatarPlaceholder: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    border: "4px solid #ffffff",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  profileName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    marginBottom: "12px",
  },
  profileSection: { marginBottom: "16px" },
  profileSectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
    margin: "0 0 8px",
  },
  profileSectionText: {
    fontSize: "13px",
    color: "#6b7280",
    margin: 0,
    lineHeight: "1.5",
  },
  profileStats: { marginBottom: "16px" },
  profileStat: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "12px",
  },
  profileStatLabel: { fontSize: "13px", color: "#6b7280", marginBottom: "2px" },
  profileStatValue: { fontSize: "14px", fontWeight: "500", color: "#111827" },
  currentOrderCard: {
    backgroundColor: "#fff7ed",
    borderRadius: "8px",
    padding: "16px",
  },
  currentOrderLabel: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "8px",
    display: "block",
  },
  currentOrderDetails: { display: "flex", flexDirection: "column" },
  currentOrderService: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  additionalBadge: { fontSize: "12px", color: "#F97316", fontWeight: "500" },
  currentOrderNumber: { fontSize: "13px", color: "#6b7280" },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
`;
document.head.appendChild(styleSheet);

export default ConversationScreen;
